import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, MessageSquarePlus, ListTodo, HelpCircle, ChevronDown } from 'lucide-react';
import api from '../../api';
import { RequestThread, STATUS_CLS } from './RequestThread';

const NewRequest = ({ token, onCreated }) => {
  const [kind, setKind] = useState('query');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api.post(`/portal/${token}/requests`, { kind, title, details });
      setTitle(''); setDetails(''); onCreated();
    } catch (x) { setErr(x?.response?.data?.detail?.[0]?.msg || x?.response?.data?.detail || 'Could not send. Please try again.'); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-xl bg-white border border-stone-200 p-5 space-y-4 h-fit" data-testid="portal-new-request">
      <div className="font-semibold text-stone-900 flex items-center gap-2"><MessageSquarePlus size={16} className="text-emerald-800"/> Ask us something</div>
      <div className="grid grid-cols-2 gap-2">
        {[['query', 'A question', HelpCircle], ['task', 'Something to do', ListTodo]].map(([k, l, I]) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`request-kind-${k}`}
            className={`rounded-lg border px-3 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors ${kind === k ? 'border-emerald-700 bg-emerald-50 text-emerald-900' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}><I size={15}/>{l}</button>
        ))}
      </div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2} maxLength={200} placeholder={kind === 'task' ? 'e.g. Please register us for VAT' : 'e.g. When is my next provisional tax due?'}
        className="w-full h-10 rounded-lg border border-stone-300 px-3 text-sm focus:outline-none focus:border-emerald-700" data-testid="request-title-input"/>
      <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} maxLength={4000} placeholder="Any details that will help us (optional)"
        className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:border-emerald-700" data-testid="request-details-input"/>
      {err && <div className="text-sm text-red-600" data-testid="request-error">{String(err)}</div>}
      <button type="submit" disabled={busy || title.trim().length < 2} className="w-full h-10 rounded-lg bg-emerald-800 text-white text-sm font-medium disabled:opacity-50 hover:bg-emerald-900 transition-colors inline-flex items-center justify-center gap-2" data-testid="request-submit-btn">
        {busy && <Loader2 size={14} className="animate-spin"/>} Send to our team
      </button>
      <p className="text-xs text-stone-500">Your accountant is notified straight away, and you'll see replies and progress here.</p>
    </form>
  );
};

const RequestCard = ({ r, open, onToggle, onSend }) => (
  <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid={`portal-request-${r.id}`}>
    <button type="button" onClick={onToggle} className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-stone-50 transition-colors">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-stone-900 truncate">{r.title}</div>
        <div className="text-xs text-stone-500 mt-0.5">{r.kind === 'task' ? 'Request' : 'Question'} · {new Date(r.createdAt).toLocaleDateString()} · {r.messages.length} repl{r.messages.length === 1 ? 'y' : 'ies'}</div>
      </div>
      <span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS_CLS[r.status]}`} data-testid={`portal-request-status-${r.id}`}>{r.status}</span>
      <ChevronDown size={16} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`}/>
    </button>
    {open && <div className="px-5 pb-5"><RequestThread req={r} side="client" onSend={onSend}/></div>}
  </div>
);

export const PortalRequests = ({ token }) => {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => api.get(`/portal/${token}/requests`).then((r) => setList(r.data)).catch(() => setList([])), [token]);
  useEffect(() => { load(); }, [load]);
  const send = async (rid, text) => {
    const r = (await api.post(`/portal/${token}/requests/${rid}/messages`, { text })).data;
    setList((l) => l.map((x) => (x.id === rid ? r : x)));
  };
  return (
    <section className="grid lg:grid-cols-[380px_1fr] gap-5" data-testid="section-requests">
      <NewRequest token={token} onCreated={load}/>
      <div className="space-y-3">
        {!list ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-stone-400"/></div>
          : list.length === 0 ? <div className="rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500" data-testid="portal-requests-empty">No questions or requests yet. Ask us anything — we'll reply right here.</div>
          : list.map((r) => <RequestCard key={r.id} r={r} open={openId === r.id} onToggle={() => setOpenId(openId === r.id ? null : r.id)} onSend={(t) => send(r.id, t)}/>)}
      </div>
    </section>
  );
};
